
/**
 * The tone vocabulary and pill geometry every evidence block shares.
 *
 * They live here rather than inside one of the five blocks because the barrel imports every block and the
 * shape switch imports all five — so any block that owned a shared helper would make its siblings import
 * *sideways* through a module that also renders. This file imports nothing from the folder, which is what
 * keeps the graph acyclic and stops `ChangeCard` importing from `LinesBlock`.
 *
 * ⚠️ No JSX here on purpose. `react-refresh/only-export-components` allows a module to export components
 * OR plain constants, and `tonePillClasses` is neither — so the component half lives in `CellValue.tsx`
 * beside this file rather than tripping the rule for the whole folder.
 */

export const EM_DASH = '—';


/** The four tone tokens the design names. Anything else is unknown, not a guess. */
export type EvidenceTone = 'success' | 'warning' | 'info' | 'destructive';

/** Shared pill geometry — status pills and the reason tag are the same object at different tints. */
export const PILL_BASE =
  'inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1 ring-inset';

/** The honest pill for a value whose tone the server did not declare. */
export const NEUTRAL_PILL = 'bg-accent text-foreground ring-border';

/** Quieter still — for a reason string, where the server withholds a tone on purpose. */
export const MUTED_PILL = 'bg-muted text-muted-foreground ring-border';

/**
 * Tone → pill classes, or `null` when the tone is absent or not one of the four.
 *
 * A **closed switch, not a lookup object**: a `Record` indexed by a wire string hands `constructor` and
 * friends back a truthy value from the prototype, which would reach `cn()` as garbage.
 *
 * Tint recipe follows the `SeverityBadge` precedent — saturated TEXT carries the meaning, the fill only
 * supports it, and a ring at /25 draws the edge. That is what keeps these legible in Industrial, where
 * `--info` and `--success` are darker and less saturated and a fill-only /10 tint nearly vanishes.
 *
 * Returning `null` rather than a default is deliberate: the callers disagree about what "no tone" means, so
 * the default belongs to each of them.
 */
export function tonePillClasses(tone: string | null | undefined): string | null {
  switch (tone) {
    case 'success':
      return 'bg-success/10 text-success ring-success/25';
    case 'warning':
      return 'bg-warning/10 text-warning ring-warning/25';
    case 'info':
      return 'bg-info/10 text-info ring-info/25';
    case 'destructive':
      return 'bg-destructive/10 text-destructive ring-destructive/25';
    default:
      return null;
  }
}
